/**
 * Turns "many readers each asking about a few phones" into "a few requests".
 *
 * A screen is made of leaves — a table cell, a chip, a card header — and each asks about the
 * phones IT shows. Everything asked for in the same tick leaves together, cut into chunks the
 * endpoint accepts, with a bound on how many chunks are out at once.
 *
 * It knows nothing about contacts, answers or errors: `send` owns all of that (it records
 * the answer into the store, or releases the ids on a failure). That split is what keeps
 * this testable with a fake `send` and a hand-cranked `defer`.
 *
 * ── The numbers ─────────────────────────────────────────────────────────────────────
 *
 * `chunkSize` is the endpoint's own cap — above it the server REFUSES rather than trims.
 * `concurrency` bounds a first paint that names thousands of phones (a year of calls): the
 * server holds a five-connection pool per instance, and one browser taking all of it would
 * stall that realtor's own other requests behind their own names.
 */
export type LookupBatcher = {
  /** Queue these ids. Everything queued before the next flush leaves as one batch. */
  request: (ids: readonly string[]) => void;
  /** Forget what has not been sent yet — an account switch must not send A's phones as B. */
  clear: () => void;
};

export type LookupBatcherOptions = {
  chunkSize: number;
  concurrency: number;
  /** Sends one chunk and settles when it is done with it. A rejection is treated as done. */
  send: (chunk: string[]) => Promise<void>;
  /** When to flush what was queued. Defaults to a microtask: "the end of this tick". */
  defer?: (flush: () => void) => void;
};

const endOfTick = (flush: () => void): void => {
  void Promise.resolve().then(flush);
};

export const createLookupBatcher = ({ chunkSize, concurrency, send, defer = endOfTick }: LookupBatcherOptions): LookupBatcher => {
  /** Asked for, not yet cut into chunks. A Set: forty cells showing one caller ask once. */
  const pending = new Set<string>();
  /** Chunks waiting for a free slot. */
  const waiting: string[][] = [];
  let flushScheduled = false;
  let active = 0;

  const pump = (): void => {
    while (active < concurrency && waiting.length > 0) {
      const chunk = waiting.shift() as string[];
      active += 1;
      // Settled either way: a failed chunk frees its slot exactly like an answered one.
      send(chunk).then(chunkDone, chunkDone);
    }
  };

  function chunkDone(): void {
    active -= 1;
    pump();
  }

  const flush = (): void => {
    flushScheduled = false;
    const ids = [...pending];
    pending.clear();
    for (let start = 0; start < ids.length; start += chunkSize) waiting.push(ids.slice(start, start + chunkSize));
    pump();
  };

  return {
    request: (ids) => {
      for (const id of ids) pending.add(id);
      if (pending.size === 0 || flushScheduled) return;
      flushScheduled = true;
      defer(flush);
    },

    clear: () => {
      pending.clear();
      waiting.length = 0;
    }
  };
};
